import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { createBenefitTypeApi, DpBenefitType, listBenefitTypesApi, updateBenefitTypeApi } from "../api/benefitsApi";
import { DpPayrollItem, listPayrollItemsApi } from "../api/payrollApi";
import { formatCurrency } from "../utils/format";

const emptyForm = {
  name: "",
  description: "",
  itemId: "",
  defaultEmployerCost: "",
  defaultEmployeeAmount: "",
  sortOrder: "100",
  isActive: true,
};

const toNumberOrNull = (value: string) => (value.trim() === "" ? null : Number(value));

/**
 * Aba "Benefícios" em Cadastros: o catálogo do que a Eco oferece (plano
 * odontológico, Gympass…). Cada benefício pode ter uma rubrica própria do DP
 * pela qual a parte do colaborador é descontada na folha — ou nenhuma, quando a
 * empresa custeia tudo.
 */
export default function BenefitTypesSettings({ token }: { token: string }) {
  const [types, setTypes] = useState<DpBenefitType[]>([]);
  const [items, setItems] = useState<DpPayrollItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ type?: DpBenefitType } | null>(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await listBenefitTypesApi(token));
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os benefícios.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    listPayrollItemsApi(token)
      .then(setItems)
      .catch(() => setItems([]));
  }, [token]);

  // Só rubrica própria do DP, de desconto e ativa — o backend recusa as outras.
  const deductionItems = useMemo(() => items.filter((i) => !i.isSystem && i.kind === "DESCONTO" && i.isActive), [items]);

  const openModal = (type?: DpBenefitType) => {
    setForm(
      type
        ? {
            name: type.name,
            description: type.description ?? "",
            itemId: type.itemId ?? "",
            defaultEmployerCost: type.defaultEmployerCost === null ? "" : String(Number(type.defaultEmployerCost)),
            defaultEmployeeAmount: type.defaultEmployeeAmount === null ? "" : String(Number(type.defaultEmployeeAmount)),
            sortOrder: String(type.sortOrder),
            isActive: type.isActive,
          }
        : emptyForm
    );
    setModal({ type });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modal) return;
    if (form.name.trim().length < 2) return toast.error("Informe o nome do benefício.");
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      itemId: form.itemId || null,
      defaultEmployerCost: toNumberOrNull(form.defaultEmployerCost),
      defaultEmployeeAmount: toNumberOrNull(form.defaultEmployeeAmount),
      sortOrder: Number(form.sortOrder) || 100,
    };
    setSaving(true);
    try {
      if (modal.type) await updateBenefitTypeApi(token, modal.type.id, { ...payload, isActive: form.isActive });
      else await createBenefitTypeApi(token, payload);
      toast.success("Benefício salvo.");
      setModal(null);
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o benefício.");
    } finally {
      setSaving(false);
    }
  };

  const itemLocked = !!modal?.type && (modal.type.assignedCount ?? 0) > 0;

  return (
    <>
      <div className="dpControls">
        <button className="dpBtn dpBtn--primary" onClick={() => openModal()}>
          Novo benefício
        </button>
      </div>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        Benefícios que a Eco oferece além de VT, VR e VA (plano odontológico, Gympass…). Depois de cadastrar aqui,
        atribua a cada colaborador na ficha, aba Contrato → "Outros benefícios". Se o colaborador paga uma parte, escolha
        a rubrica da folha pela qual o desconto sai — crie-a em Rubricas, como desconto.
      </p>

      {loading ? (
        <div className="dpEmpty">Carregando...</div>
      ) : (
        <div className="dpTableWrapper">
          <table className="dpTable">
            <thead>
              <tr>
                <th>Benefício</th>
                <th>Desconto do colaborador</th>
                <th>Custo sugerido (empresa)</th>
                <th>Colaboradores</th>
                <th style={{ width: 90 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {types.length === 0 ? (
                <tr>
                  <td colSpan={5} className="dpEmpty">
                    Nenhum benefício cadastrado.
                  </td>
                </tr>
              ) : (
                types.map((type) => (
                  <tr key={type.id} style={type.isActive ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{type.name}</strong>
                      {type.description ? <div className="dpFormHint">{type.description}</div> : null}
                      {!type.isActive ? <div className="dpFormHint">Inativo</div> : null}
                    </td>
                    <td>
                      {type.item ? (
                        <>
                          {type.item.name}
                          <div className="dpFormHint">
                            {type.defaultEmployeeAmount !== null
                              ? `Sugerido: ${formatCurrency(type.defaultEmployeeAmount)}/mês`
                              : `Rubrica ${type.item.code}`}
                          </div>
                        </>
                      ) : (
                        <span className="dpBadge dpBadge--gray">Custeado pela empresa</span>
                      )}
                    </td>
                    <td>{type.defaultEmployerCost !== null ? `${formatCurrency(type.defaultEmployerCost)}/mês` : "—"}</td>
                    <td>{type.assignedCount ?? 0}</td>
                    <td>
                      <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => openModal(type)}>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal">
            <header className="dpModal__header">
              <h2 className="dpModal__title">{modal.type ? "Editar benefício" : "Novo benefício"}</h2>
              <button className="dpModal__close" onClick={() => setModal(null)}>
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                <div className="dpFormGroup">
                  <label htmlFor="btName">Nome *</label>
                  <input
                    id="btName"
                    className="dpInput"
                    maxLength={120}
                    placeholder="Plano odontológico"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    disabled={saving}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="btDescription">Descrição</label>
                  <input
                    id="btDescription"
                    className="dpInput"
                    maxLength={500}
                    placeholder="Operadora, plano, o que cobre…"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    disabled={saving}
                  />
                </div>
                <div className="dpFormGroup">
                  <label htmlFor="btItem">Desconto do colaborador pela rubrica</label>
                  <select
                    id="btItem"
                    className="dpSelect"
                    value={form.itemId}
                    onChange={(e) => setForm({ ...form, itemId: e.target.value })}
                    disabled={saving || itemLocked}
                  >
                    <option value="">Sem desconto — custeado pela empresa</option>
                    {deductionItems.map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({i.code})
                      </option>
                    ))}
                    {/* Rubrica atual que saiu do filtro (ex.: inativada): continua visível. */}
                    {modal.type?.item && !deductionItems.some((i) => i.id === modal.type?.item?.id) ? (
                      <option value={modal.type.item.id}>{modal.type.item.name}</option>
                    ) : null}
                  </select>
                  <span className="dpFormHint">
                    {itemLocked
                      ? `Benefício já atribuído a ${modal.type?.assignedCount} colaborador(es): a rubrica não muda. Para outra rubrica, crie outro benefício.`
                      : "Só rubricas próprias do DP, de desconto (Cadastros → Rubricas). Sem rubrica, nada sai na folha."}
                  </span>
                </div>
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="btEmployer">Custo mensal da empresa (R$)</label>
                    <input
                      id="btEmployer"
                      type="number"
                      step="0.01"
                      min="0"
                      className="dpInput"
                      value={form.defaultEmployerCost}
                      onChange={(e) => setForm({ ...form, defaultEmployerCost: e.target.value })}
                      disabled={saving}
                    />
                    <span className="dpFormHint">Sugestão ao atribuir; só informativo.</span>
                  </div>
                  {form.itemId ? (
                    <div className="dpFormGroup">
                      <label htmlFor="btEmployee">Desconto mensal sugerido (R$)</label>
                      <input
                        id="btEmployee"
                        type="number"
                        step="0.01"
                        min="0"
                        className="dpInput"
                        value={form.defaultEmployeeAmount}
                        onChange={(e) => setForm({ ...form, defaultEmployeeAmount: e.target.value })}
                        disabled={saving}
                      />
                      <span className="dpFormHint">Pode ser ajustado por colaborador.</span>
                    </div>
                  ) : null}
                </div>
                <div className="dpFormGrid dpFormGrid--two">
                  <div className="dpFormGroup">
                    <label htmlFor="btOrder">Ordem</label>
                    <input
                      id="btOrder"
                      type="number"
                      step="1"
                      min="0"
                      className="dpInput"
                      value={form.sortOrder}
                      onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  {modal.type ? (
                    <div className="dpFormGroup" style={{ justifyContent: "flex-end" }}>
                      <label className="dpCheckboxRow">
                        <input
                          type="checkbox"
                          checked={form.isActive}
                          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                          disabled={saving}
                        />{" "}
                        Ativo (inativo não aparece para novas atribuições)
                      </label>
                    </div>
                  ) : null}
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  Salvar
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
